/**
 * The game loop — now driven by the deterministic CORE sim.
 *
 * The authoritative simulation is `core/world.ts`: each fixed 60 Hz step we
 * advance a {@link WorldState} via {@link stepWorld} (feeding it the player's
 * pointer target), and the {@link PatternDirector} renders `world.hazards` by
 * reconciling pooled sprites against the descriptors core produced. Collision,
 * motion, spawning and scoring ALL live in core — Phaser only draws.
 *
 * The run seed is generated HERE (the game layer) with the platform RNG and
 * passed to both {@link createWorld} and the {@link RunRecorder}; core itself
 * never calls `Math.random`. The recorder captures the player's input timeline
 * so the run is a replayable {@link RunRecord} (anti-cheat + replays).
 *
 * React never sees per-frame state: the live score is drawn IN the canvas; only
 * the one-shot `gameover` event (score + mode + deathFamily) crosses the bus.
 *
 * Fixed-timestep: player follow, spawn scheduling, hazard motion and scoring all
 * run on a 60 Hz {@link FixedStepClock}; delta is clamped and the scene pauses on
 * visibilitychange to avoid time jumps after backgrounding.
 */
import Phaser from 'phaser';
import type { GameMode, Hazard, PlayerInput, Viewport, WorldState } from '../core';
import {
  PLAYER,
  SIM,
  CANONICAL_VIEWPORT,
  FixedStepClock,
  fixedDeltaSeconds,
  scoreFromElapsedMs,
  speedStepForElapsed,
  createWorld,
  advanceHazardsCosmetic,
} from '../core';
import { PatternDirector } from './PatternDirector';
import { RunRecorder } from './RunRecorder';
import { Background } from './Background';
import { ensureHazardTextures } from './textures';
import { Player } from './Player';
import { getSkin } from './skins';
import { fitCameraToCanonical, alignHud } from './viewport';
import type { GameBus, GameCommand, GameOverPayload } from './events';

/**
 * Session-best key. Stored in localStorage (NOT the per-Phaser.Game registry) so the
 * in-canvas BEST survives a "Play Again" — which now remounts a fresh Phaser.Game
 * (and would otherwise start each restart's registry empty).
 */
const SESSION_BEST_KEY = 'barrage.sessionBest';

/** Read/write the session best from localStorage, guarded (private mode can throw). */
function loadSessionBest(): number {
  try {
    return Number(localStorage.getItem(SESSION_BEST_KEY)) || 0;
  } catch {
    return 0;
  }
}
function saveSessionBest(n: number): void {
  try {
    localStorage.setItem(SESSION_BEST_KEY, String(n));
  } catch {
    /* localStorage unavailable — the React all-time best is the source of truth anyway. */
  }
}

/** Death-sequence playout: minimum hold (the explosion must read) and a hard cap. */
const DEATH_MIN_MS = 650;
const DEATH_MAX_MS = 2000;

/** Generate a fresh 32-bit run seed using the platform RNG (allowed in the game
 * layer — NOT inside core). Passed into `createWorld` + `RunRecorder`. */
function makeSeed(): number {
  return (Math.random() * 0x1_0000_0000) >>> 0;
}

export class MainScene extends Phaser.Scene {
  private readonly bus: GameBus;
  private readonly skinId: string;
  private readonly mode: GameMode;
  private readonly clock = new FixedStepClock();

  private player!: Player;
  private director!: PatternDirector;
  private recorder!: RunRecorder;
  private background!: Background;
  private world!: WorldState;

  /** HUD overlay container — counter-transformed so it stays upright over the
   *  zoomed/rotated world camera (children authored in screen px). */
  private hud!: Phaser.GameObjects.Container;
  private scoreText!: Phaser.GameObjects.Text;
  private bestText!: Phaser.GameObjects.Text;
  private countdownText?: Phaser.GameObjects.Text;
  /** Reusable "SPEED INCREASED!" flash + the last speed step we announced. */
  private speedText!: Phaser.GameObjects.Text;
  private lastSpeedStep = 0;

  private playing = false;
  /** True during the post-death playout (ball exploded, attacks finishing). */
  private dying = false;
  private deathMs = 0;
  /** The authoritative game-over payload, snapshotted at the death frame. */
  private pendingGameOver: GameOverPayload | null = null;
  /** First sim step seeds the world player at the first input (no snap-to-centre). */
  private firstStep = true;
  /** Where the ball was when the run was committed (from the drag), if any. */
  private readonly startX?: number;
  private readonly startY?: number;
  /** Forced run seed (Daily Run: the day's shared seed) instead of a random roll. */
  private readonly forcedSeed?: number;
  private targetX = 0;
  private targetY = 0;
  private commandUnsub: (() => void) | null = null;
  /** Guard so the external-listener cleanup runs exactly once (SHUTDOWN or DESTROY). */
  private cleanedUp = false;

  constructor(
    bus: GameBus,
    skinId: string,
    mode: GameMode,
    startX?: number,
    startY?: number,
    forcedSeed?: number,
  ) {
    super({ key: 'main' });
    this.bus = bus;
    this.skinId = skinId;
    this.mode = mode;
    this.startX = startX;
    this.startY = startY;
    this.forcedSeed = forcedSeed;
  }

  create(): void {
    // Everyone plays the SAME canonical field (fairness); the main camera scales it to
    // the device and ROTATES 90° on portrait. The sim, the record and the render all
    // use canonical coordinates — pointer.worldX/Y maps back into them automatically.
    const view: Viewport = { width: CANONICAL_VIEWPORT.width, height: CANONICAL_VIEWPORT.height };
    fitCameraToCanonical(this);
    // Arcade world bounds = the canonical field, so the ball body matches core's clamp.
    this.physics.world.setBounds(0, 0, view.width, view.height);

    ensureHazardTextures(this);

    // Arena backdrop, sized to the canonical field (behind everything; decorative).
    this.background = new Background(this);
    this.background.resize(view.width, view.height);

    // The ball starts where the drag left off (canonical coords) — NOT dead centre —
    // so the hand-off from the start-screen drag flows naturally with no snap-back.
    const startX = Phaser.Math.Clamp(this.startX ?? view.width / 2, 0, view.width);
    const startY = Phaser.Math.Clamp(this.startY ?? view.height / 2, 0, view.height);

    // Player ball with its googly face (rendering only; collision is in core).
    this.player = new Player(this, startX, startY, getSkin(this.skinId));

    // Hazard renderer — draws core's `world.hazards` (no game rules here).
    this.director = new PatternDirector(this);

    // The authoritative deterministic world + the input-timeline recorder. The
    // SAME seed feeds both so the server can re-simulate the recorded run. A
    // forced seed (Daily Run) replaces the random roll — nothing else changes.
    const seed = this.forcedSeed ?? makeSeed();
    this.world = createWorld(seed, view, this.mode);
    this.recorder = new RunRecorder(seed, view, this.mode);

    // In-canvas HUD lives in a counter-transformed CONTAINER so it stays UPRIGHT and
    // correctly sized over the zoomed/rotated world camera (children authored in SCREEN
    // px from the top-left). A glassy teal-edged panel with a big bold score + BEST.
    const fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
    const { width: cw, height: ch } = this.scale.gameSize;
    this.hud = this.add.container(0, 0).setDepth(25);
    const hudPanel = this.add.graphics();
    hudPanel.fillStyle(0x07181d, 0.55);
    hudPanel.fillRoundedRect(10, 8, 156, 64, 13);
    hudPanel.lineStyle(1.5, 0x4fe3f0, 0.45);
    hudPanel.strokeRoundedRect(10, 8, 156, 64, 13);
    this.scoreText = this.add.text(24, 13, '0', {
      fontFamily,
      fontSize: '34px',
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#0c3b44',
      strokeThickness: 5,
    });
    this.scoreText.setShadow(0, 2, '#000000', 6, true, true);
    this.bestText = this.add.text(26, 51, 'BEST 0', {
      fontFamily,
      fontSize: '13px',
      color: '#f2c84b',
      fontStyle: 'bold',
    });

    // "SPEED INCREASED!" flash, centred on the SCREEN (repositioned on resize). Hidden
    // until the difficulty ramp steps up.
    this.speedText = this.add
      .text(cw / 2, ch * 0.42, '', {
        fontFamily,
        fontSize: '34px',
        color: '#ffd166',
        fontStyle: 'bold',
        stroke: '#1a1030',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setVisible(false);
    this.lastSpeedStep = 0;
    this.hud.add([hudPanel, this.scoreText, this.bestText, this.speedText]);
    alignHud(this, this.hud);

    // Pointer follow target starts at the drag-in position; the world player is seeded
    // there too (the sim then snaps to the first real input on the first live step).
    this.world.playerX = startX;
    this.world.playerY = startY;
    this.targetX = startX;
    this.targetY = startY;
    this.input.on('pointermove', this.onPointer, this);
    this.input.on('pointerdown', this.onPointer, this);

    // External listeners that Phaser does NOT auto-clean on shutdown.
    this.scale.on('resize', this.onResize, this);
    document.addEventListener('visibilitychange', this.handleVisibility);
    this.commandUnsub?.();
    this.commandUnsub = this.bus.onCommand(this.onCommand);
    this.cleanedUp = false;
    // Clean up external listeners on SHUTDOWN (scene.restart) AND DESTROY — the React
    // shell remounts the whole Phaser.Game on Play Again / Menu, and game.destroy()
    // fires only DESTROY (never SHUTDOWN), so without this the document visibilitychange
    // listener + the app-lifetime GameBus subscription would leak on every navigation.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown, this);
    this.events.once(Phaser.Scenes.Events.DESTROY, this.onShutdown, this);

    // (Re)start run state. A brief 3-2-1 countdown (legacy `countdownstartingfunc`,
    // analysis §1.1) holds the sim still so the player can settle on the ball
    // before the barrage begins; the run then goes live.
    this.clock.reset();
    this.playing = false;
    this.dying = false;
    this.deathMs = 0;
    this.firstStep = true;
    this.pendingGameOver = null;
    this.director.sync(this.world); // initial paint (no hazards yet)
    this.refreshHud();
    this.startCountdown();
  }

  /** In-canvas 3-2-1 countdown, then flip the sim live. Drawn by Phaser (no React). */
  private startCountdown(): void {
    const { width: cw, height: ch } = this.scale.gameSize;
    const font = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
    this.countdownText?.destroy();
    this.countdownText = this.add
      .text(cw / 2, ch * 0.32, '', {
        fontFamily: font,
        fontSize: '88px',
        color: '#e8ecff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.hud.add(this.countdownText);

    const tick = (n: number): void => {
      if (n <= 0) {
        this.countdownText?.destroy();
        this.countdownText = undefined;
        // Reset the clock so the held time doesn't count, then go live.
        this.clock.reset();
        this.playing = true;
        return;
      }
      this.countdownText?.setText(String(n)).setScale(1).setAlpha(1);
      this.tweens.add({
        targets: this.countdownText,
        scale: 1.4,
        alpha: 0.2,
        duration: 600,
        ease: 'Quad.easeIn',
      });
      this.time.delayedCall(700, () => tick(n - 1));
    };
    tick(3);
  }

  private onPointer = (pointer: Phaser.Input.Pointer): void => {
    // Only follow while the pointer is HELD DOWN (click/touch-and-drag). A hovering
    // mouse cursor (move with no button) must NOT drag the ball around.
    if (!pointer.isDown) return;
    this.targetX = pointer.worldX;
    this.targetY = pointer.worldY - PLAYER.pointerOffsetY;
  };

  private onResize = (gameSize: Phaser.Structs.Size): void => {
    // The world viewport stays CANONICAL (the field is device-independent + the same
    // for every player) — only the camera + HUD re-fit to the new device canvas.
    fitCameraToCanonical(this);
    if (this.hud) {
      alignHud(this, this.hud);
      this.speedText.setPosition(gameSize.width / 2, gameSize.height * 0.42);
      this.countdownText?.setPosition(gameSize.width / 2, gameSize.height * 0.32);
    }
  };

  private onCommand = (cmd: GameCommand): void => {
    // NOTE: 'restart' is retired here — the React shell now restarts by remounting a
    // fresh canvas (App.onRestart → directRun), which is reliable; the bus command is
    // no longer sent. pause/resume remain for tab-visibility / future transport use.
    switch (cmd) {
      case 'pause':
        this.scene.pause();
        break;
      case 'resume':
        this.clock.reset();
        this.scene.resume();
        break;
    }
  };

  private handleVisibility = (): void => {
    if (document.hidden) {
      this.scene.pause();
    } else {
      this.clock.reset();
      this.scene.resume();
    }
  };

  /**
   * The player just died. Snapshot the authoritative outcome NOW (score locked at
   * the death frame for anti-cheat), blow the ball up dramatically, and enter the
   * death playout — the sim is over but the in-flight attacks keep animating
   * (cosmetic only) until {@link finishDeath} emits the game-over.
   */
  private beginDeath(): void {
    this.playing = false;
    this.dying = true;
    this.deathMs = 0;

    const score = scoreFromElapsedMs(this.world.elapsedMs);
    const prevBest = loadSessionBest();
    const best = Math.max(prevBest, score);
    saveSessionBest(best);
    this.scoreText.setText(score.toLocaleString());
    this.bestText.setText(`BEST ${best.toLocaleString()}`);

    // Seal the input timeline at the death frame so the recorded length matches the
    // real run length — otherwise a held-still finish would re-sim short on the server
    // (false anti-cheat rejection) and cut off early in replays.
    this.recorder.seal(this.world.elapsedMs);

    this.pendingGameOver = {
      score,
      mode: this.world.mode,
      deathFamily: this.world.deathFamily,
      record: this.recorder.toRecord(),
    };

    // Burst the ball in its skin colours, then hide it (the explosion replaces it).
    const skin = getSkin(this.skinId);
    this.player.setDead();
    this.director.explodePlayer(
      this.world.playerX,
      this.world.playerY,
      PLAYER.radius,
      skin.body,
      skin.highlight,
      skin.rim,
    );
    this.player.hide();
  }

  /**
   * Cosmetically ease the (not-yet-live) ball toward the pointer during the countdown,
   * so it flows from the start-screen drag instead of snapping/freezing. No sim here —
   * the run goes live (and seeds its true start) only when the countdown ends.
   */
  private coastBall(delta: number): void {
    const view = this.world.viewport;
    const tx = Phaser.Math.Clamp(this.targetX, 0, view.width);
    const ty = Phaser.Math.Clamp(this.targetY, 0, view.height);
    const k = 0.18;
    this.player.setPosition(this.player.x + (tx - this.player.x) * k, this.player.y + (ty - this.player.y) * k);
    this.player.update(Math.min(delta, 100) / 1000, null, PLAYER.eyeLookRange);
  }

  /** Advance the cosmetic post-death playout; emit game-over when it's done. */
  private updateDeath(delta: number): void {
    const steps = this.clock.step(delta);
    for (let i = 0; i < steps; i++) {
      advanceHazardsCosmetic(this.world, SIM.fixedDeltaMs);
    }
    this.director.sync(this.world);
    this.deathMs += Math.min(delta, SIM.maxFrameMs);

    // End once the attacks have finished (or we hit the cap), after a minimum hold.
    const done = this.world.hazards.length === 0 || this.deathMs >= DEATH_MAX_MS;
    if (this.deathMs >= DEATH_MIN_MS && done) {
      this.finishDeath();
    }
  }

  /** Close out the run: emit the snapshotted game-over to React (once). */
  private finishDeath(): void {
    if (!this.dying) return;
    this.dying = false;
    if (this.pendingGameOver) {
      this.bus.emitGameOver(this.pendingGameOver);
      this.pendingGameOver = null;
    }
  }

  private onShutdown = (): void => {
    if (this.cleanedUp) return; // idempotent (may fire on both SHUTDOWN and DESTROY)
    this.cleanedUp = true;
    this.scale.off('resize', this.onResize, this);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.commandUnsub?.();
    this.commandUnsub = null;
    this.countdownText?.destroy();
    this.countdownText = undefined;
    this.director?.destroy();
    this.background?.destroy();
  };

  override update(_time: number, delta: number): void {
    // The backdrop drifts/twinkles even during the countdown (capped so a long tab
    // background doesn't jump the starfield).
    this.background.update(Math.min(delta, 100) / 1000);
    // Post-death playout: the ball has exploded; let the attacks finish, then end.
    if (this.dying) {
      this.updateDeath(delta);
      return;
    }
    // Pre-game (the 3-2-1 countdown): cosmetically ease the ball toward the pointer so
    // the hand-off from the start-screen drag flows naturally (no freeze, no snap).
    if (!this.playing) {
      this.coastBall(delta);
      return;
    }
    const steps = this.clock.step(delta);
    if (steps === 0) return;

    for (let i = 0; i < steps; i++) {
      this.simStep();
      if (!this.playing) break; // a hit during this frame ends the run
    }
    this.refreshHud();
  }

  /** One fixed 60 Hz simulation step: record input, advance core, render, sync face. */
  private simStep(): void {
    // Resolve this step's pointer target, clamped to the viewport (the same space
    // core clamps the player into). Record it on the sim clock for replay.
    const view = this.world.viewport;
    const input: PlayerInput = {
      x: Phaser.Math.Clamp(this.targetX, 0, view.width),
      y: Phaser.Math.Clamp(this.targetY, 0, view.height),
    };
    // Seed the run at the first real input so the player starts exactly where they
    // are (no centre snap). runSimulation does the same, keeping the re-sim in sync.
    if (this.firstStep) {
      this.firstStep = false;
      this.world.playerX = input.x;
      this.world.playerY = input.y;
    }

    this.recorder.sample(this.world.elapsedMs, input.x, input.y);

    // Advance the authoritative world one fixed step and reconcile the visuals.
    this.director.step(this.world, input);

    // Mirror the core player position onto the rendered ball + its face.
    this.player.setPosition(this.world.playerX, this.world.playerY);
    const danger = this.findNearestDanger(PLAYER.eyeLookRange);
    this.player.update(fixedDeltaSeconds, danger, PLAYER.eyeLookRange);

    // Announce each difficulty step-up (the elapsed-time speed ramp) with a flash.
    const step = speedStepForElapsed(this.world.elapsedMs);
    if (step > this.lastSpeedStep) {
      this.lastSpeedStep = step;
      this.flashSpeedUp();
    }

    // Collision is computed in core; the run ends when the world reports death.
    if (!this.world.alive) {
      this.beginDeath();
    }
  }

  /** Flash "SPEED INCREASED!" in-canvas when the difficulty ramp steps up. */
  private flashSpeedUp(): void {
    const { width, height } = this.scale.gameSize;
    const t = this.speedText;
    this.tweens.killTweensOf(t);
    t.setText('SPEED INCREASED!')
      .setPosition(width / 2, height * 0.42)
      .setVisible(true)
      .setAlpha(1)
      .setScale(0.6);
    // Pop in (scale), hold, then fade out — two non-conflicting tweens.
    this.tweens.add({ targets: t, scale: 1.12, duration: 320, ease: 'Back.easeOut' });
    this.tweens.add({
      targets: t,
      alpha: 0,
      delay: 850,
      duration: 450,
      onComplete: () => t.setVisible(false),
    });
  }

  /**
   * Nearest live hazard centre within `lookRange` for the googly-eye lock-on.
   * Uses a representative point per hazard kind (purely cosmetic; not collision).
   */
  private findNearestDanger(lookRange: number): { x: number; y: number } | null {
    const px = this.world.playerX;
    const py = this.world.playerY;
    let best: { x: number; y: number } | null = null;
    let bestDist = lookRange;
    for (const h of this.world.hazards) {
      const p = hazardCenter(h);
      const dist = Math.hypot(p.x - px, p.y - py);
      if (dist < bestDist) {
        bestDist = dist;
        best = p;
      }
    }
    return best;
  }

  private refreshHud(): void {
    const score = scoreFromElapsedMs(this.world.elapsedMs);
    const best = loadSessionBest();
    this.scoreText.setText(score.toLocaleString());
    this.bestText.setText(`BEST ${Math.max(best, score).toLocaleString()}`);
  }
}

/** A representative centre point for a hazard (used only for the eye lock-on). */
function hazardCenter(h: Hazard): { x: number; y: number } {
  switch (h.kind) {
    case 'rect':
      return { x: h.x + h.w / 2, y: h.y + h.h / 2 };
    case 'segment':
      return { x: (h.x1 + h.x2) / 2, y: (h.y1 + h.y2) / 2 };
    case 'rotatingSegment':
      return { x: h.cx, y: h.cy };
    case 'circleMover':
      return { x: h.x, y: h.y };
  }
}
